import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ConfBadge, EmptyState, PageHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

export default function Reports() {
  const { tenantId, can, access } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [report, setReport] = useState("panchatanthra_scores");
  const [conf, setConf] = useState("1");
  const [active, setActive] = useState(null);

  const load = () => {
    if (!tenantId) return;
    api.get(`/tenants/${tenantId}/exports`).then(({ data }) => setJobs(data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(load, [tenantId]); // eslint-disable-line

  const run = async () => {
    try {
      const { data } = await api.post(`/tenants/${tenantId}/exports`, {
        report,
        confidentiality: Number(conf),
        framework: report === "panchatanthra_scores" ? "panchatanthra" : null,
      });
      setActive(data);
      toast.success(`Export ready — ${data.row_count} rows`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const download = (job) => {
    const rows = job.rows || [];
    if (!rows.length) return toast.error("Nothing to download");
    const keys = Object.keys(rows[0]);
    const csv = [keys.join(","), ...rows.map((r) => keys.map((k) => JSON.stringify(r[k] ?? "")).join(","))].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${job.report}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        testid="reports-header"
        eyebrow="Security boundary"
        title="Report centre"
        description="Exports are permission-filtered at generation time and blocked outright for classes whose policy disallows export."
        actions={
          can("export") && (
            <div className="flex flex-wrap items-center gap-3">
              <Select value={report} onValueChange={setReport}>
                <SelectTrigger data-testid="report-type-select" className="h-9 w-56 rounded-none text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="panchatanthra_scores">Panchatanthra score snapshots</SelectItem>
                  <SelectItem value="action_items">Commitments & actions</SelectItem>
                </SelectContent>
              </Select>
              <Select value={conf} onValueChange={setConf}>
                <SelectTrigger data-testid="report-confidentiality-select" className="h-9 w-36 rounded-none text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {[1, 2, 3].filter((l) => l <= (access?.confidentiality_max || 1)).map((l) => (
                    <SelectItem key={l} value={String(l)}>{["", "General", "Sensitive", "Restricted"][l]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button data-testid="generate-report-button" className="h-9 rounded-none text-[10px] uppercase tracking-[0.18em]" onClick={run}>
                Generate export
              </Button>
            </div>
          )
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-1">
          <p className="label-caps mb-4">Export jobs</p>
          <div className="panel divide-y divide-border" data-testid="export-jobs-list">
            {jobs.map((j) => (
              <button
                key={j.id}
                data-testid={`export-job-${j.id}`}
                onClick={() => setActive(j)}
                className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left transition-colors hover:bg-muted"
              >
                <div>
                  <p className="text-sm">{j.report.replace(/_/g, " ")}</p>
                  <p className="label-caps mt-1">{j.row_count} rows · {j.status}</p>
                </div>
                <ConfBadge level={j.confidentiality} />
              </button>
            ))}
            {jobs.length === 0 && <p className="px-5 py-8 text-sm text-muted-foreground">No exports generated.</p>}
          </div>
        </div>

        <div className="lg:col-span-2">
          <p className="label-caps mb-4">Preview</p>
          {active ? (
            <div className="panel overflow-x-auto" data-testid="export-preview">
              <div className="flex items-center justify-between border-b border-border px-5 py-3">
                <p className="text-sm">{active.report.replace(/_/g, " ")}</p>
                <Button
                  data-testid="download-export-button"
                  size="sm"
                  variant="outline"
                  className="rounded-none text-[10px] uppercase tracking-[0.15em]"
                  onClick={() => download(active)}
                >
                  Download CSV
                </Button>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    {Object.keys(active.rows?.[0] || {}).map((k) => (
                      <TableHead key={k} className="label-caps">{k}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(active.rows || []).slice(0, 40).map((r, i) => (
                    <TableRow key={i}>
                      {Object.keys(active.rows[0]).map((k) => (
                        <TableCell key={k} className="font-mono text-xs tabular-nums">{String(r[k] ?? "")}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {(active.rows || []).length === 0 && (
                <p className="px-5 py-8 text-sm text-muted-foreground">No rows at your clearance.</p>
              )}
            </div>
          ) : (
            <EmptyState testid="reports-empty" title="No export selected" message="Generate or select an export job to preview permitted rows." />
          )}
        </div>
      </div>
    </div>
  );
}
